// ==================== Domain Types ====================

export interface PhoneNumber {
  id: number;
  phone_number: string;
}

export interface Customer {
  id: number;
  phone_number?: PhoneNumber;
  addresses?: CustomerAddress[];
}

export interface CustomerAddress {
  id?: number;
  address?: string;
  floor?: string;
  name?: string;
  contact_phone?: string;
  extra?: string;
  customer?: Customer;
  last_used_at?: string;
  order_count?: number;
  lat?: number | null;
  lng?: number | null;
}

export interface Call {
  id: number;
  uuid: string;
  incoming_at: string;
  answered_at: string | null;
  finished_at: string | null;
  caller_number: string;
  caller_number_id?: number;
  agent: number | null;
  agent_count: number;
  recording_url: string | null;
  active: boolean;
  customer?: Customer | null;
  delivery: number[];
}

export interface CallQuality {
  quality_score: 'good' | 'fair' | 'poor' | 'critical' | 'unknown';
  packet_loss_pct: number;
  has_one_way_audio: number;
  has_high_jitter: number;
  has_packet_loss: number;
  rtp_in_quality_percentage: number;
  duration: number;
}

export interface MenuItem {
  id: number;
  sort_id: number;
  name: string;
  short_name?: string;
  description?: string;
  /** Widget-only translations keyed by language code; base name is the default. */
  name_i18n?: Record<string, string> | null;
  description_i18n?: Record<string, string> | null;
  /** This item's POS id in Wolt Marketplace (link layer for inbound Wolt orders). */
  wolt_pos_id?: string | null;
  price: number;
  active: boolean;
  combo: boolean;
  option: boolean;
  visible_online: boolean;
  visible_offline?: boolean;
  ui_color: number;
  deleted?: boolean;
  img_url?: string | null;
}

export interface MenuGroup {
  id: number;
  name: string;
  active: boolean;
  base: boolean;
  number_of_pieces: number;
  price_algorithm_max: boolean;
  description?: string;
  /** Widget-only translations keyed by language code; base name is the default. */
  name_i18n?: Record<string, string> | null;
  description_i18n?: Record<string, string> | null;
  multi?: boolean;
  default_menu_item_id?: number | null;
  visible_online?: boolean;
  visible_offline?: boolean;
  deleted?: boolean;
  img_url?: string | null;
}

export interface MenuItemGroupMember {
  menu_group_id: number;
  menu_item_id: number;
}

export interface MenuGroupGroupComplement {
  menu_group_id: number;
  complement_menu_group_id: number;
}

export interface MenuItemGroupComplement {
  menu_item_id: number;
  complement_menu_group_id: number;
}

export interface Location {
  id: number;
  name: string;
  address?: string;
  description?: string;
  requires_delivery?: boolean;
  active?: boolean;
  deleted?: boolean;
  lat?: number | null;
  lng?: number | null;
  is_default?: boolean;
  /** When true (default) deliveries at this location auto-print on create/approve;
   *  when false the operator prints the ticket manually later. */
  auto_print?: boolean;
  /** Wolt Drive pickup venue id for this location. */
  wolt_venue_id?: string | null;
}

export interface Agent {
  id: number;
  agent_name: string;
  agent_key: string;
}

export interface Deliveryman {
  id: number;
  name: string;
  vehicle?: string;
  barcode?: string;
  active?: boolean;
  deleted?: boolean;
}

export interface Order {
  id?: number;
  menu_item_id?: number;
  menu_item?: MenuItem;
  count: number;
  price?: number;
  gratis?: boolean;
  // Backend fields
  parent_order_item_id?: number | null;
  delivery_id?: number;
  note?: string | null;
  addons?: Order[];
  // Cart-specific fields
  uuid?: string;
  sibling_list?: string[];
  main_part?: boolean;
  total_price?: number;
  tainted?: boolean;
  group_id?: number;
  price_algorithm_max?: boolean;
  number_of_pieces?: number;
  combo?: boolean;
}

export interface Delivery {
  id: number;
  uuid: string;
  created_at: string;
  dispatched_at: string | null;
  canceled: boolean;
  canceled_at: string | null;
  canceled_reason?: string;
  amount?: number;
  printed: boolean;
  cc_payment?: boolean;
  online_paid?: boolean;
  // Online card payment lifecycle: pending | authorized | paid | failed | voided | refunded
  payment_status?: string | null;
  payment_intent_id?: string | null;
  online_order?: boolean;
  approved?: boolean | null;
  approved_at?: string | null;
  rejected_reason?: string;
  // Online order we could not accept (validation reason, payment problem, or
  // technical error). Visible to operators; money already released/refunded and
  // the customer emailed. error_acknowledged = operator dismissed it.
  errored?: boolean;
  error_reason?: string | null;
  error_acknowledged?: boolean;
  estimated_delivery_minutes?: number | null;
  discount?: number;
  delayed?: boolean;
  delayed_time?: string | null;
  note?: string;
  customer_address?: CustomerAddress;
  location?: Location;
  call?: Call;
  deliveryman?: number;
  orders: Order[];
  // Inbound source: 'wolt' = Wolt Marketplace; else web (online_order) / phone.
  source?: string | null;
  woltmp_order_id?: string | null;
  woltmp_status?: string | null;
  // Wolt Drive last-mile courier (quote + dispatch state)
  wolt_shipment_promise_id?: string | null;
  wolt_fee?: number | null;
  wolt_estimated_minutes?: number | null;
  wolt_quoted_at?: string | null;
  wolt_delivery_id?: string | null;
  wolt_status?: string | null;
  wolt_tracking_url?: string | null;
  wolt_dispatched_at?: string | null;
}

export interface Checkin {
  location: number;
  deliveryman: number;
  time: string;
}

// ==================== Cart Types ====================

export interface CartItem {
  uuid: string;
  id: number;
  name: string;
  price: number;
  total_price: number;
  gratis: boolean;
  tainted?: boolean;
  combo?: boolean;
  main_part: boolean;
  group_id?: number;
  price_algorithm_max?: boolean;
  number_of_pieces?: number;
  addons?: CartItem[];
  sibling_list?: string[];
  sort_id?: number;
  description?: string;
  active?: boolean;
  option?: boolean;
  visible_online?: boolean;
  ui_color?: number;
}

export interface CartState {
  cartItems: Record<string, CartItem>;
  selectedItem: string;
  partialItems: {
    cartItems: Record<string, CartItem>;
    group_key: number | null;
    price_algo_max: boolean;
    number_of_pieces: number | null;
  };
}

// ==================== UI Types ====================

export interface UIVisibleComponents {
  ui_delay: boolean;
  ui_gratis: boolean;
  ui_nth_gratis: boolean;
  ui_loyalty: boolean;
  ui_mini_stats: boolean;
  ui_distance: boolean;
  ui_discount: boolean;
  ui_payment_opts: boolean;
  ui_map_overlay: boolean;
  ui_call_stats: boolean;
  ui_tablet_view: boolean;
  ui_view_deliveries: boolean;
  ui_view_calls: boolean;
  ui_view_handoff: boolean;
  ui_view_menu: boolean;
  ui_view_deliverymen: boolean;
  ui_mute_online_order_sound: boolean;
  ui_view_online_toggle: boolean;
  ui_view_min_delivery_time: boolean;
  ui_table_remove_items: boolean;
  ui_customer_note: boolean;
}

export interface CustomerNote {
  id: number;
  text: string;
}

export interface DeliveryAddressDistance {
  distances: Record<number, number>;
}

export interface FeedbackMessage {
  id: string;
  message: string;
  severity: MessageSeverity;
  timeout: number;
  autoRemove?: boolean;
  sound?: string;
}

export enum MessageSeverity {
  SUCCESS = 'SUCCESS',
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum PaymentType {
  CASH = 'cache', // kept as 'cache' for backend compatibility
  CC = 'cc',
}

// ==================== Stats Types ====================

export interface LocationBalanceStat {
  id: number;
  name: string;
  scanned: number;
  not_scanned: number;
  total: number;
  total_cc: number;
  total_online: number;
  delivery_count: number;
}

export interface LocationBalanceStats {
  stats: LocationBalanceStat[];
  from_date: string;
  to_date: string;
}

export interface DeliveryManAddress {
  delivery_id: number;
  address: string;
  cc_payment: boolean;
  online_paid: boolean;
  amount: number;
  canceled: boolean;
}

export interface DeliveryManLocation {
  location_id: number;
  location: string;
  addresses: DeliveryManAddress[];
}

export interface DeliveryManStat {
  id: number;
  name: string;
  locations: DeliveryManLocation[];
  total: number;
  total_cc: number;
  total_online: number;
  points: number;
}

export interface DeliveryMenStats {
  stats: DeliveryManStat[];
  from_date: string;
  to_date: string;
}

export interface ItemSoldStat {
  menu_item_id: number;
  count: number;
  gratis: boolean;
}

export interface ItemsSoldStats {
  stats: ItemSoldStat[];
  from_date: string;
  to_date: string;
}

// ==================== WebSocket Response ====================

export interface WsResponse<T = unknown> {
  status: {
    code: number;
    message: string;
  };
  payload?: T;
}

// ==================== Trends Types ====================

export interface TrendsRevenueVolume {
  period: string;
  revenue: number;
  order_count: number;
}

export interface TrendsPhoneVsOnline {
  period: string;
  phone_count: number;
  phone_revenue: number;
  online_count: number;
  online_revenue: number;
}

export interface TrendsPaymentMethod {
  period: string;
  cash_count: number;
  cash_revenue: number;
  cc_count: number;
  cc_revenue: number;
  online_paid_count?: number;
  online_paid_revenue?: number;
}

export interface TrendsDiscount {
  period: string;
  discount_total: number;
  discount_count: number;
  gratis_count: number;
}

export interface TrendsRevenueKPI {
  total_revenue: number;
  order_count: number;
  avg_order: number;
}

export interface TrendsRevenueResponse {
  revenue_volume: TrendsRevenueVolume[];
  phone_vs_online: TrendsPhoneVsOnline[];
  payment_methods: TrendsPaymentMethod[];
  discounts: TrendsDiscount[];
  kpi: TrendsRevenueKPI;
}

export interface TrendsHeatmapEntry {
  day: number;
  hour: number;
  count: number;
}

export interface TrendsHourlyEntry {
  hour: number;
  count: number;
}

export interface TrendsDeliverySpeed {
  period: string;
  avg_bake_minutes: number;
  avg_dispatch_minutes: number;
}

export interface TrendsCancellationRate {
  period: string;
  total: number;
  canceled: number;
  rate_pct: number;
}

export interface TrendsCancellationReason {
  reason: string;
  count: number;
}

export interface TrendsOperationsResponse {
  peak_hours: {
    heatmap: TrendsHeatmapEntry[];
    hourly: TrendsHourlyEntry[];
  };
  delivery_speed: TrendsDeliverySpeed[];
  cancellations: {
    rate: TrendsCancellationRate[];
    reasons: TrendsCancellationReason[];
  };
}

export interface TrendsItemByQuantity {
  menu_item_id: number;
  name: string;
  count: number;
}

export interface TrendsItemByRevenue {
  menu_item_id: number;
  name: string;
  revenue: number;
}

export interface TrendsItemByCategory {
  group_id: number;
  group_name: string;
  count: number;
}

export interface TrendsItemsResponse {
  by_quantity: TrendsItemByQuantity[];
  by_revenue: TrendsItemByRevenue[];
  by_category: TrendsItemByCategory[];
}

export interface TrendsCallVolume {
  period: string;
  total_calls: number;
  answered_calls: number;
}

export interface TrendsCallConversion {
  period: string;
  calls_with_order: number;
  total_calls: number;
  rate_pct: number;
}

export interface TrendsCallsResponse {
  volume: TrendsCallVolume[];
  conversion: TrendsCallConversion[];
  kpi_conversion_pct: number;
}

export interface TrendsNewVsReturning {
  period: string;
  new_customers: number;
  returning_customers: number;
}

export interface TrendsTopCustomer {
  customer_id: number;
  phone: string;
  name: string;
  order_count: number;
  total_revenue: number;
}

export interface TrendsCustomersResponse {
  new_vs_returning: TrendsNewVsReturning[];
  top_customers: TrendsTopCustomer[];
}

export interface TrendsDriver {
  deliveryman_id: number;
  name: string;
  delivery_count: number;
  revenue: number;
}

export interface TrendsAgent {
  agent_id: number;
  agent_name: string;
  order_count: number;
  revenue: number;
}

export interface TrendsPersonnelResponse {
  drivers: TrendsDriver[];
  agents: TrendsAgent[];
}

// ==================== Auth Types ====================

export interface JwtPayload {
  public_id: string;
  email: string;
  admin: boolean;
  exp: number;
}

// ==================== Table Ordering Types ====================

export type TableShape = 'rectangle' | 'square' | 'circle';
export type PaymentMethod = 'cash' | 'card';

export interface Zone {
  id: number;
  name: string;
  location_id?: number | null;
  ordinal: number;
  canvas_width: number;
  canvas_height: number;
}

export interface RestaurantTable {
  id: number;
  zone_id: number;
  name: string;
  seats: number;
  shape: TableShape;
  layout_x: number;
  layout_y: number;
  layout_width: number;
  layout_height: number;
  layout_rotation: number;
  active: boolean;
}

/** Table tile on the floor plan: layout + live occupancy from its open session. */
export interface FloorTable extends RestaurantTable {
  is_occupied: boolean;
  current_total: number;
  paid_total: number;
  session_id: number | null;
  owner: string | null;
}

/** One persisted line on a table (with addons + full menu_item incl. price). */
export interface TableLineItem {
  id: number;
  menu_item_id: number;
  menu_item: MenuItem;
  gratis: boolean;
  note?: string | null;
  table_bill_id: number | null;
  addons?: TableLineItem[];
}

export interface TableBill {
  id: number;
  table_session_id: number;
  amount: number;
  payment_method: PaymentMethod;
  paid_at?: string | null;
}

export interface TableSession {
  id: number;
  table_id: number;
  status: 'open' | 'closed';
  orders: TableLineItem[];
  bills: TableBill[];
  total: number;
  paid_total: number;
  unpaid_total: number;
  owner: string | null;
  table_name?: string | null;
  zone_name?: string | null;
  opened_at?: string | null;
  closed_at?: string | null;
}
